/**
 * Sales Urgency Components - Conversion Optimization
 *
 * Features:
 * - Countdown timer for campaign end dates
 * - Stock urgency messages
 * - Live activity indicator (simulated)
 * - Free shipping progress bar
 */

/**
 * Countdown Timer Component
 * Usage: <div x-data="countdownTimer('2024-12-31 23:59:59')" x-show="!expired">
 *          <span x-text="display"></span>
 *        </div>
 */
export function countdownTimer(endDate) {
  return {
    endDate: endDate,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
    display: '',
    interval: null,

    init() {
      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    },

    destroy() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    },

    updateCountdown() {
      const now = new Date().getTime();
      const end = new Date(this.endDate).getTime();
      const distance = end - now;

      if (distance < 0) {
        this.expired = true;
        this.display = window.tx('saleEnded', 'Sale ended');
        if (this.interval) {
          clearInterval(this.interval);
        }
        return;
      }

      this.days = Math.floor(distance / (1000 * 60 * 60 * 24));
      this.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      this.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      this.seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Format display based on time remaining
      if (this.days > 0) {
        this.display = `${this.days}g ${this.hours}s ${this.minutes}d`;
      } else if (this.hours > 0) {
        this.display = `${this.hours}s ${this.minutes}d ${this.seconds}sn`;
      } else {
        this.display = `${this.minutes}d ${this.seconds}sn`;
      }
    },

    // Check if urgency level (< 24 hours)
    get isUrgent() {
      return this.days === 0 && this.hours < 24;
    },

    // Check if critical (< 1 hour)
    get isCritical() {
      return this.days === 0 && this.hours === 0;
    }
  };
}

/**
 * Stock Urgency Component
 * Usage: <div x-data="stockUrgency(5)">
 *          <span x-show="showUrgency" x-text="message"></span>
 *        </div>
 */
export function stockUrgency(quantity) {
  return {
    quantity: quantity,
    showUrgency: false,
    message: '',
    urgencyLevel: 'normal', // normal, low, critical

    init() {
      this.updateUrgency();
    },

    updateUrgency() {
      if (this.quantity <= 0) {
        this.showUrgency = false;
        return;
      }

      if (this.quantity <= 3) {
        this.urgencyLevel = 'critical';
        this.showUrgency = true;
        this.message = window.tx('stockCritical', 'Son') + ' ' + this.quantity + ' ' + window.tx('stockLeft', 'adet kaldı!');
      } else if (this.quantity <= 10) {
        this.urgencyLevel = 'low';
        this.showUrgency = true;
        this.message = window.tx('stockLow', 'Sadece') + ' ' + this.quantity + ' ' + window.tx('stockRemaining', 'adet stokta');
      } else {
        this.urgencyLevel = 'normal';
        this.showUrgency = false;
      }
    }
  };
}

/**
 * Free Shipping Progress Component
 * Shows progress toward free shipping threshold
 * Usage: <div x-data="freeShippingProgress(150, 89.90)">
 *          <div x-show="!reached">...</div>
 *        </div>
 */
export function freeShippingProgress(threshold, currentTotal) {
  return {
    threshold: threshold,
    currentTotal: currentTotal,
    remaining: 0,
    percentage: 0,
    reached: false,

    init() {
      this.calculate();
    },

    calculate() {
      this.remaining = Math.max(0, this.threshold - this.currentTotal);
      this.percentage = Math.min(100, (this.currentTotal / this.threshold) * 100);
      this.reached = this.currentTotal >= this.threshold;
    },

    // Update when cart changes
    update(newTotal) {
      this.currentTotal = newTotal;
      this.calculate();
    },

    get formattedRemaining() {
      return this.remaining.toFixed(2).replace('.', ',');
    }
  };
}

/**
 * Simple Countdown Component for Product Cards
 * Takes a timestamp in milliseconds and shows remaining time
 * Usage: <div x-data="countdown(1735689599000)" x-init="start()" x-show="remaining > 0">
 *          <span x-text="days"></span>d <span x-text="hours"></span>:<span x-text="minutes"></span>:<span x-text="seconds"></span>
 *        </div>
 */
export function countdown(endTimestamp) {
  return {
    endTimestamp: endTimestamp,
    remaining: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    interval: null,
    confettiFired: false,

    start() {
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    },

    destroy() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    },

    update() {
      const now = Date.now();
      this.remaining = Math.max(0, this.endTimestamp - now);

      if (this.remaining <= 0) {
        this.days = 0;
        this.hours = 0;
        this.minutes = 0;
        this.seconds = 0;
        if (this.interval) {
          clearInterval(this.interval);
        }
        if (!this.confettiFired) {
          this.confettiFired = true;
          this.fireConfetti();
        }
        return;
      }

      const totalSeconds = Math.floor(this.remaining / 1000);
      this.days = Math.floor(totalSeconds / 86400);
      this.hours = Math.floor((totalSeconds % 86400) / 3600);
      this.minutes = Math.floor((totalSeconds % 3600) / 60);
      this.seconds = totalSeconds % 60;
    },

    fireConfetti() {
      const el = this.$el;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff922b', '#cc5de8'];
      const count = 60;

      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 6 + 4;
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5);
        const velocity = 80 + Math.random() * 120;
        const dx = Math.cos(angle) * velocity;
        const dy = Math.sin(angle) * velocity - 40;
        const color = colors[Math.floor(Math.random() * colors.length)];
        const rotation = Math.random() * 360;

        Object.assign(particle.style, {
          position: 'fixed',
          left: cx + 'px',
          top: cy + 'px',
          width: size + 'px',
          height: size + 'px',
          backgroundColor: color,
          borderRadius: Math.random() > 0.5 ? '50%' : '0',
          pointerEvents: 'none',
          zIndex: '9999',
          transform: `rotate(${rotation}deg)`,
        });
        document.body.appendChild(particle);

        particle.animate([
          { transform: `translate(0, 0) rotate(${rotation}deg)`, opacity: 1 },
          { transform: `translate(${dx}px, ${dy + 200}px) rotate(${rotation + 360}deg)`, opacity: 0 },
        ], { duration: 800 + Math.random() * 400, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' })
        .onfinish = () => particle.remove();
      }
    }
  };
}

export default {
  countdownTimer,
  stockUrgency,
  freeShippingProgress,
  countdown
};
